import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = 'https://65aad0f563267f0894012c7b7d65a0fc.hackmap.win';

// --- Helper functions for Local Storage ---
const getFromStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.error("Error reading from localStorage", error);
    return defaultValue;
  }
};

const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Error saving to localStorage", error);
  }
};

// --- Icon Components (for a cleaner look) ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>;
const MinusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>;
const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>;
const ArchiveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg>;


// --- Main App Component ---
function App() {
  // --- State Management ---
  const [words, setWords] = useState(() => getFromStorage('words', []));
  const [categories, setCategories] = useState([]);
  const [wordGroups, setWordGroups] = useState([]);
  const [activeSession, setActiveSession] = useState(() => getFromStorage('activeSession', null));
  const [currentView, setCurrentView] = useState(() => getFromStorage('currentView', 'dashboard'));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [wordToEdit, setWordToEdit] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const wsRef = useRef(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);

  // --- Effects for saving data to Local Storage ---
  useEffect(() => { saveToStorage('words', words); }, [words]);
  useEffect(() => { saveToStorage('activeSession', activeSession); }, [activeSession]);
  useEffect(() => { saveToStorage('currentView', currentView); }, [currentView]);

  useEffect(() => {
    fetchWords();
    fetchCategories();
    fetchWordGroups();
  }, [showArchived, selectedCategory, selectedGroup]);

  const fetchWords = async () => {
    try {
      const params = new URLSearchParams({
        include_archived: showArchived,
        ...(selectedCategory && { category_id: selectedCategory }),
        ...(selectedGroup && { group_id: selectedGroup })
      });
      const response = await axios.get(`${API_URL}/words/?${params}`);
      setWords(response.data);
    } catch (error) {
      console.error('Error fetching words:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories/`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchWordGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/word-groups/`);
      setWordGroups(response.data);
    } catch (error) {
      console.error('Error fetching word groups:', error);
    }
  };

  // --- Event Handlers ---
  const handleAddOrUpdateWord = async (wordData) => {
    try {
      if (wordToEdit) {
        await axios.put(`${API_URL}/words/${wordToEdit.id}`, wordData);
      } else {
        await axios.post(`${API_URL}/words/`, wordData);
      }
      fetchWords();
      closeModal();
    } catch (error) {
      console.error('Error saving word:', error);
    }
  };

  const handleDeleteWord = async (id) => {
    if (window.confirm("Are you sure you want to delete this word and its progress?")) {
      try {
        await axios.delete(`${API_URL}/words/${id}`);
        setWords(words.filter(w => w.id !== id));
        if (activeSession?.id === id) {
          setActiveSession(null);
          setCurrentView('dashboard');
        }
      } catch (error) {
        console.error('Error deleting word:', error);
      }
    }
  };

  const handleArchiveWord = async (id) => {
    try {
      await axios.patch(`${API_URL}/words/${id}/archive`);
      setWords(words.map(word => 
        word.id === id ? { ...word, is_archived: true } : word
      ));
    } catch (error) {
      console.error('Error archiving word:', error);
    }
  };

  const handleUnarchiveWord = async (id) => {
    try {
      await axios.patch(`${API_URL}/words/${id}/unarchive`);
      setWords(words.map(word => 
        word.id === id ? { ...word, is_archived: false } : word
      ));
    } catch (error) {
      console.error('Error unarchiving word:', error);
    }
  };

  const handleStartSession = (word) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(`wss://65aad0f563267f0894012c7b7d65a0fc.hackmap.win/ws/${word.id}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'update') {
        setActiveSession(prev => ({
          ...prev,
          count: data.data.count
        }));
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    const sessionToStart = (activeSession && activeSession.id === word.id) ? activeSession : { ...word, count: 0 };
    setActiveSession(sessionToStart);
    setCurrentView('session');
  };
  
  const handleIncrement = () => {
    if (!activeSession || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'increment' }));
  };

  const handleDecrement = () => {
    if (!activeSession || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'decrement' }));
  };
  
  const handleBackToDashboard = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setCurrentView('dashboard');
  };

  const openModal = (word = null) => {
    setWordToEdit(word);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setWordToEdit(null);
  };

  const handleCreateCategory = async (name) => {
    try {
      await axios.post(`${API_URL}/categories/`, { name });
      fetchCategories();
      setIsCategoryModalOpen(false);
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleCreateWordGroup = async (name) => {
    try {
      await axios.post(`${API_URL}/word-groups/`, { name });
      fetchWordGroups();
      setIsGroupModalOpen(false);
    } catch (error) {
      console.error('Error creating word group:', error);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm("Are you sure you want to delete this category? Words in this category will need to be reassigned.")) {
      try {
        await axios.delete(`${API_URL}/categories/${id}`);
        fetchCategories();
      } catch (error) {
        if (error.response?.status === 400) {
          alert(error.response.data.detail);
        } else {
          console.error('Error deleting category:', error);
        }
      }
    }
  };

  const handleDeleteWordGroup = async (id) => {
    if (window.confirm("Are you sure you want to delete this word group?")) {
      try {
        await axios.delete(`${API_URL}/word-groups/${id}`);
        fetchWordGroups();
      } catch (error) {
        if (error.response?.status === 400) {
          alert(error.response.data.detail);
        } else {
          console.error('Error deleting word group:', error);
        }
      }
    }
  };

  // --- Render Logic ---
  return (
    <div className="bg-gray-900 text-white min-h-screen font-sans">
      <div className="container mx-auto p-4 md:p-8 max-w-4xl">
        <header className="text-center mb-8 sm:mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-cyan-400">Neuro-Implant</h1>
          <p className="text-gray-400 mt-2">Your experimental language learning tool.</p>
        </header>

        {(currentView === 'session' && activeSession) ? (
          <CounterScreen 
            session={activeSession} 
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onBack={handleBackToDashboard} 
          />
        ) : (
          <Dashboard 
            words={words}
            categories={categories}
            wordGroups={wordGroups}
            onStartSession={handleStartSession}
            onEdit={openModal}
            onDelete={handleDeleteWord}
            onArchive={handleArchiveWord}
            onUnarchive={handleUnarchiveWord}
            onAddWord={() => openModal()}
            activeSession={activeSession}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            onCreateCategory={() => setIsCategoryModalOpen(true)}
            onCreateGroup={() => setIsGroupModalOpen(true)}
            onDeleteCategory={handleDeleteCategory}
            onDeleteGroup={handleDeleteWordGroup}
          />
        )}

        {isModalOpen && (
          <AddEditWordModal 
            wordToEdit={wordToEdit}
            categories={categories}
            wordGroups={wordGroups}
            onSave={handleAddOrUpdateWord} 
            onClose={closeModal} 
          />
        )}

        {isCategoryModalOpen && (
          <CreateCategoryModal
            onSave={handleCreateCategory}
            onClose={() => setIsCategoryModalOpen(false)}
          />
        )}

        {isGroupModalOpen && (
          <CreateGroupModal
            onSave={handleCreateWordGroup}
            onClose={() => setIsGroupModalOpen(false)}
          />
        )}

        <footer className="text-center mt-12 sm:mt-16 text-gray-500 text-sm">
          <p>Inspired by a crazy idea. Built for focused repetition.</p>
        </footer>
      </div>
    </div>
  );
}

// --- Dashboard Component ---
const Dashboard = ({ 
  words, 
  categories,
  wordGroups,
  onStartSession, 
  onEdit, 
  onDelete,
  onArchive,
  onUnarchive,
  onAddWord, 
  activeSession,
  showArchived,
  setShowArchived,
  selectedCategory,
  setSelectedCategory,
  selectedGroup,
  setSelectedGroup,
  onCreateCategory,
  onCreateGroup,
  onDeleteCategory,
  onDeleteGroup
}) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl sm:text-3xl font-semibold">Your Word List</h2>
        <button onClick={onAddWord} className="flex items-center bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 px-3 sm:px-4 rounded-lg transition-colors text-sm sm:text-base">
          <PlusIcon /> <span className="ml-2 hidden sm:inline">Add New Word</span>
        </button>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <select 
              value={selectedCategory || ''} 
              onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
              className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
            <button 
              onClick={onCreateCategory}
              className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"
              title="Create Category"
            >
              <PlusIcon />
            </button>
            {selectedCategory && (
              <button 
                onClick={() => onDeleteCategory(selectedCategory)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                title="Delete Category"
              >
                <TrashIcon />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select 
              value={selectedGroup || ''} 
              onChange={(e) => setSelectedGroup(e.target.value ? Number(e.target.value) : null)}
              className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Groups</option>
              {wordGroups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
            <button 
              onClick={onCreateGroup}
              className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"
              title="Create Group"
            >
              <PlusIcon />
            </button>
            {selectedGroup && (
              <button 
                onClick={() => onDeleteGroup(selectedGroup)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                title="Delete Group"
              >
                <TrashIcon />
              </button>
            )}
          </div>

          <button 
            onClick={() => setShowArchived(!showArchived)}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              showArchived 
                ? 'bg-yellow-500 border-yellow-600 hover:bg-yellow-600' 
                : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
            }`}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </button>
        </div>
      </div>

      {words.length === 0 ? (
        <div className="text-center py-10 bg-gray-800 rounded-lg">
          <h3 className="text-xl font-semibold text-white">Your list is empty.</h3>
          <p className="text-gray-400 mt-2">Add your first word to begin the experiment!</p>
          <button onClick={onAddWord} className="mt-6 bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
            Add a Word
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {words.map(word => {
            const progress = (activeSession && activeSession.id === word.id) ? activeSession : { count: 0 };
            const isCompleted = progress.count >= word.target;
            const progressPercent = Math.min((progress.count / word.target) * 100, 100);

            return (
              <div key={word.id} className={`bg-gray-800 p-4 rounded-lg shadow-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${word.is_archived ? 'opacity-50' : ''}`}>
                <div className="flex-grow">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-gray-100">{word.phrase}</h3>
                    {word.is_archived && (
                      <span className="text-xs bg-yellow-500 text-yellow-900 px-2 py-1 rounded">Archived</span>
                    )}
                  </div>
                  <p className="text-gray-400">{word.meaning}</p>
                  {word.category && (
                    <span className="inline-block bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded mt-2">
                      {word.category.name}
                    </span>
                  )}
                  {word.groups && word.groups.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {word.groups.map(group => (
                        <span key={group.id} className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">
                          {group.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2">
                    <div className="w-full bg-gray-700 rounded-full h-2.5">
                      <div className={`h-2.5 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-cyan-500'}`} style={{width: `${progressPercent}%`}}></div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{progress.count} / {word.target} reps</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3 self-end sm:self-center">
                  <button onClick={() => onEdit(word)} className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"><EditIcon /></button>
                  {word.is_archived ? (
                    <button onClick={() => onUnarchive(word.id)} className="p-2 text-gray-400 hover:text-green-400 transition-colors"><ArchiveIcon /></button>
                  ) : (
                    <button onClick={() => onArchive(word.id)} className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"><ArchiveIcon /></button>
                  )}
                  <button onClick={() => onDelete(word.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><TrashIcon /></button>
                  <button onClick={() => onStartSession(word)} className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 sm:px-6 rounded-lg transition-colors">
                    {progress.count > 0 ? 'Continue' : 'Start'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
};

// --- Counter Screen Component ---
const CounterScreen = ({ session, onIncrement, onDecrement, onBack }) => {
  const progress = (session.count / session.target) * 100;

  return (
    <div className="flex flex-col items-center">
      <button onClick={onBack} className="self-start mb-6 flex items-center text-cyan-400 hover:text-cyan-300 transition-colors">
        <BackIcon/> Back to Dashboard
      </button>

      <div className="text-center mb-6">
        <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white break-words">{session.phrase}</h2>
        <p className="text-2xl md:text-3xl text-gray-400 mt-2">{session.meaning}</p>
      </div>

      <div className="w-full space-y-4">
        <button 
          onClick={onIncrement} 
          className="w-full h-56 sm:h-64 bg-gray-800 rounded-2xl flex flex-col items-center justify-center cursor-pointer select-none
                     shadow-2xl transform active:scale-95 transition-transform duration-100 ease-out focus:outline-none ring-2 ring-gray-700 focus:ring-cyan-500"
        >
          <div className="text-7xl sm:text-8xl font-mono font-bold text-cyan-400">{session.count}</div>
          <div className="text-xl sm:text-2xl text-gray-500">/ {session.target}</div>
        </button>

        <div className="flex justify-center space-x-4">
          <button 
            onClick={onDecrement}
            className="p-4 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <MinusIcon />
          </button>
        </div>
      </div>

      <div className="w-full bg-gray-700 rounded-full h-4 mt-8 overflow-hidden">
        <div 
          className="bg-cyan-500 h-4 rounded-full transition-all duration-300 ease-out" 
          style={{ width: `${Math.min(progress, 100)}%` }}
        ></div>
      </div>
      <p className="mt-2 text-sm text-gray-400">{Math.round(progress)}% complete</p>
    </div>
  );
};

// --- Add/Edit Modal Component ---
const AddEditWordModal = ({ wordToEdit, categories, wordGroups, onSave, onClose }) => {
  const [phrase, setPhrase] = useState(wordToEdit?.phrase || '');
  const [meaning, setMeaning] = useState(wordToEdit?.meaning || '');
  const [target, setTarget] = useState(wordToEdit?.target || 500);
  const [categoryId, setCategoryId] = useState(wordToEdit?.category_id || '');
  const [selectedGroups, setSelectedGroups] = useState(wordToEdit?.groups?.map(g => g.id) || []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!phrase.trim() || !meaning.trim()) {
      alert("Phrase and Meaning cannot be empty.");
      return;
    }
    onSave({ 
      phrase, 
      meaning, 
      target: Number(target),
      category_id: categoryId ? Number(categoryId) : null,
      group_ids: selectedGroups
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 sm:p-8 w-full max-w-md shadow-2xl">
        <h2 className="text-2xl font-bold mb-6">{wordToEdit ? 'Edit Word' : 'Add New Word'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-400 mb-2" htmlFor="phrase">Word / Phrase</label>
            <input 
              type="text" 
              id="phrase" 
              value={phrase} 
              onChange={e => setPhrase(e.target.value)} 
              className="w-full p-3 bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500" 
              required 
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-400 mb-2" htmlFor="meaning">Meaning</label>
            <input 
              type="text" 
              id="meaning" 
              value={meaning} 
              onChange={e => setMeaning(e.target.value)} 
              className="w-full p-3 bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500" 
              required 
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-400 mb-2" htmlFor="category">Category</label>
            <select 
              id="category"
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="w-full p-3 bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">No Category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-gray-400 mb-2">Word Groups</label>
            <div className="space-y-2">
              {wordGroups.map(group => (
                <label key={group.id} className="flex items-center space-x-2">
                  <input 
                    type="checkbox"
                    checked={selectedGroups.includes(group.id)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedGroups([...selectedGroups, group.id]);
                      } else {
                        setSelectedGroups(selectedGroups.filter(id => id !== group.id));
                      }
                    }}
                    className="form-checkbox h-5 w-5 text-cyan-500 rounded border-gray-600 focus:ring-cyan-500"
                  />
                  <span>{group.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="mb-6">
            <label className="block text-gray-400 mb-2" htmlFor="target">Repetition Target</label>
            <input 
              type="number" 
              id="target" 
              value={target} 
              onChange={e => setTarget(e.target.value)} 
              min="1" 
              className="w-full p-3 bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500" 
              required 
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button type="button" onClick={onClose} className="py-2 px-6 bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors">Cancel</button>
            <button type="submit" className="py-2 px-6 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-lg transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateCategoryModal = ({ onSave, onClose }) => {
  const [name, setName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert("Category name cannot be empty.");
      return;
    }
    onSave(name);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 sm:p-8 w-full max-w-md shadow-2xl">
        <h2 className="text-2xl font-bold mb-6">Create New Category</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-400 mb-2" htmlFor="categoryName">Category Name</label>
            <input 
              type="text" 
              id="categoryName" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              className="w-full p-3 bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500" 
              required 
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button 
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-lg transition-colors"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateGroupModal = ({ onSave, onClose }) => {
  const [name, setName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert("Group name cannot be empty.");
      return;
    }
    onSave(name);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 sm:p-8 w-full max-w-md shadow-2xl">
        <h2 className="text-2xl font-bold mb-6">Create New Word Group</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-400 mb-2" htmlFor="groupName">Group Name</label>
            <input 
              type="text" 
              id="groupName" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              className="w-full p-3 bg-gray-700 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500" 
              required 
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button 
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-lg transition-colors"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default App;
